import type { ReactNode } from "react"
import { FileSearch, RotateCw, ServerCrash, ShieldAlert, WifiOff } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { ApiError } from "@/lib/api"
import { vozDeError, type Ambito, type Idioma } from "@/lib/marty"
import { cn } from "@/lib/utils"
import { useVoz, useVozDe } from "@/lib/voz"
import { t } from "@/lib/i18n"

interface DataStateProps {
  loading?: boolean
  error?: unknown
  empty?: boolean
  /**
   * Titulo del vacio. Debe decir que falta, no "sin datos".
   *
   * Si no se pasa, se usa la frase que Marty tiene para ESTA seccion
   * (`lib/marty.ts`). Pasarlo es para el vacio que depende de algo que solo
   * sabe la pantalla — «tus filtros no devuelven nada» no es lo mismo que «no
   * hay nada que revisar», y la diferencia solo la conoce quien puso el filtro.
   */
  emptyMessage?: string
  /** Que hacer a continuacion. Un vacio sin salida es un callejon. */
  emptyHint?: ReactNode
  emptyAction?: ReactNode
  /** Reintento real: vuelve a disparar la query. */
  onRetry?: () => void
  children: ReactNode
  rows?: number
  /** Anchos relativos de las columnas del esqueleto: imita la tabla real. */
  columns?: number[]
  /**
   * Fuerza el ambito de la voz. Por defecto se deduce de la RUTA. Se pasa
   * cuando el panel no habla de la pantalla en la que vive: un dialogo abierto
   * desde el diario que carga un plan de cuentas no esta cargando un diario.
   */
  ambito?: Ambito
  className?: string
}

/**
 * Carga / error / vacio para las pantallas de trabajo.
 *
 * Tres reglas:
 * - Cargando: el esqueleto tiene la forma de la tabla que viene. No hay salto
 *   de layout cuando llegan los datos, ni un spinner mudo en el centro. Y lo
 *   que se lee mientras espera es lo que Marty diria de ESTA seccion: «Marty
 *   está sumando el mayor…», no «Consultando el libro…».
 * - Error: se dice QUE fallo, QUE significa para el trabajo y COMO seguir.
 *   "Error inesperado" no es un mensaje, es una renuncia.
 * - Vacio: se distingue "no hay nada que revisar" (buena noticia) de
 *   "tus filtros no devuelven nada" (accion pendiente).
 *
 * Las tres frases salen de `lib/marty.ts`, en el idioma de la SOCIEDAD activa.
 * Ver `lib/voz.tsx` para por que llegan por contexto y no por prop.
 */
export function DataState({
  loading,
  error,
  empty,
  emptyMessage,
  emptyHint,
  emptyAction,
  onRetry,
  children,
  rows = 10,
  columns,
  ambito,
  className,
}: DataStateProps) {
  const contexto = useVoz()
  // `useVozDe` con el ambito ya resuelto del contexto es la identidad, asi que
  // llamar siempre a los dos hooks no cuesta nada y evita un hook condicional.
  const voz = useVozDe(ambito ?? contexto.ambito)

  if (loading) {
    const widths = columns ?? [14, 8, 26, 12, 10, 10, 10, 10]
    return (
      <div className={cn("animate-row-in p-2", className)} aria-busy="true" aria-live="polite">
        <div className="mb-2 flex items-center gap-1.5 px-0.5 text-2xs text-muted-foreground">
          <RotateCw className="h-3 w-3 animate-spin [animation-duration:1.2s]" />
          {voz.carga}
        </div>
        <div className="space-y-[3px]">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <div key={rowIndex} className="flex gap-2" style={{ opacity: 1 - rowIndex * 0.07 }}>
              {widths.map((width, columnIndex) => (
                <Skeleton
                  key={columnIndex}
                  className="h-[1.125rem]"
                  style={{ width: `${width}%` }}
                />
              ))}
            </div>
          ))}
        </div>
        <span className="sr-only">{voz.carga}</span>
      </div>
    )
  }

  if (error) {
    const detail = describeError(error, contexto.idioma)
    return (
      <div
        role="alert"
        className={cn(
          "m-2 flex items-start gap-3 rounded-md border border-destructive/35 bg-destructive-soft p-3",
          className,
        )}
      >
        <detail.Icon className="mt-px h-4 w-4 shrink-0 text-destructive" />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-semibold text-destructive">{detail.title}</p>
          <p className="text-xs leading-[1.15rem] text-foreground/80">{detail.help}</p>
          {detail.raw ? (
            <p className="code pt-0.5 text-2xs text-muted-foreground">{detail.raw}</p>
          ) : null}
          {onRetry ? (
            <div className="pt-1.5">
              <Button size="xs" variant="outline" onClick={onRetry}>
                <RotateCw />
                {t("Reintentar")}</Button>
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  if (empty) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-2 px-6 py-14 text-center",
          className,
        )}
      >
        <div className="grid h-9 w-9 place-items-center rounded-full border border-dashed border-border-strong text-muted-foreground">
          <FileSearch className="h-4 w-4" />
        </div>
        <p className="max-w-[46ch] text-sm font-medium text-foreground">
          {emptyMessage ?? voz.vacio.titulo}
        </p>
        {/*
          La pista por defecto es la de Marty para esta seccion. Se pinta salvo
          que la pantalla haya puesto la suya: dos explicaciones seguidas de lo
          mismo se leen como un error de la aplicacion.
        */}
        {emptyHint ?? voz.vacio.pista ? (
          <p className="max-w-[52ch] text-xs leading-[1.15rem] text-muted-foreground">
            {emptyHint ?? voz.vacio.pista}
          </p>
        ) : null}
        {emptyAction ? <div className="pt-1">{emptyAction}</div> : null}
      </div>
    )
  }

  return <>{children}</>
}

interface ErrorDetail {
  title: string
  help: string
  raw?: string
  Icon: typeof ServerCrash
}

/**
 * Un 403 y un 500 no se resuelven igual: el primero es un permiso que hay que
 * pedir, el segundo un incidente que hay que reportar. Decirlo ahorra un ticket.
 *
 * El TEXTO vive en `lib/marty.ts` —traducido a los tres idiomas y en la voz de
 * Marty—; aca se decide nada mas el icono y que detalle crudo se muestra. Un
 * fallo es justo el momento en el que menos conviene que el producto cambie de
 * tono.
 */
export function describeError(error: unknown, idioma: Idioma = "es"): ErrorDetail {
  if (error instanceof ApiError) {
    const raw = typeof error.detail === "string" ? error.detail : undefined
    const voz = vozDeError({ estado: error.status }, idioma)
    // El 422 y el 409 traen en `detail` el campo o la regla que fallo: sin eso
    // el mensaje dice "corrige ese campo" sin decir cual.
    const conDetalle = error.status === 422 || error.status === 409 || error.status >= 500
    const Icon =
      error.status === 401 || error.status === 403 || error.status === 409
        ? ShieldAlert
        : error.status === 404
          ? FileSearch
          : ServerCrash
    return {
      title: voz.titulo,
      help: voz.ayuda,
      raw: conDetalle ? raw : undefined,
      Icon,
    }
  }

  const message = error instanceof Error ? error.message : String(error ?? "")
  if (/fetch|network|failed to fetch/i.test(message)) {
    const voz = vozDeError({ esDeRed: true }, idioma)
    return { title: voz.titulo, help: voz.ayuda, raw: message, Icon: WifiOff }
  }
  const voz = vozDeError({}, idioma)
  return { title: voz.titulo, help: voz.ayuda, raw: message || undefined, Icon: ServerCrash }
}
